import { DatePipe, UpperCasePipe } from '@angular/common'
import {
	ChangeDetectionStrategy,
	Component,
	input,
	InputSignal,
	OnDestroy,
	OnInit,
	output,
	OutputEmitterRef,
	signal,
	WritableSignal,
	inject,
} from '@angular/core'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { HighlightDirective } from '@shared/directives/higlight.directive'
import { TuiIcon } from '@taiga-ui/core'
import { finalize, Subject, takeUntil } from 'rxjs'
import { MarketStatusEnum } from '@modules/alpaca-module/market-status/enums/market-status.enum'
import { PositionOptionInterface } from '@modules/options-module/positions/interfaces/position-option.interface'
import { OptionTypeEnum } from '@modules/options-module/general/enums/option-type.enum'
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms'
import { TuiInputModule, TuiTextfieldControllerModule } from '@taiga-ui/legacy'
import { AlpacaAccountInterface } from '@modules/alpaca-module/alpaca-account/interfaces/alpaca-account.interface'
import { HttpErrorResponse } from '@angular/common/http'
import { OptionSharesPerContractConstant } from '@modules/options-module/general/constants/option-shares-per-contract.constant'
import { OrderTypeSelectComponent } from '@shared/components/trading/orders/common/order-type-select/order-type-select.component'
import { OrderTypeEnum } from '@modules/orders-module/enums/order-type.enum'
import { OptionsService } from '@modules/options-module/general/services/options.service'
import { OptionContractDetailsParamsInterface } from '@modules/options-module/general/interfaces/option-contract-details-params.Interface'
import { OptionContractDetailsInterface } from '@modules/options-module/general/interfaces/option-contract-details.interface'
import { ClosePositionInterface } from '@modules/positions-module/interfaces/close-position.interface'
import { OrderTermEnum } from '@modules/orders-module/enums/order-term.enum'
import { AssetClassEnum } from '@modules/positions-module/enums/asset-class.enum'
import { MarketStatusHelperService } from '@modules/alpaca-module/market-status/services/market-status-helper.service'
import { PositionsOptionsService } from '@modules/options-module/positions/services/positions-options.service'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { PricePipe } from '@shared/pipes/price.pipe'
import { QuantityPipe } from '@shared/pipes/quantity.pipe'
import { AlertService } from '@shared/services/alert.service'
import { AlpacaAccountHelperService } from '@modules/alpaca-module/alpaca-account/services/alpaca-account-helper.service'
import { ButtonComponent } from '@shared/components/ui-kit/button/button.component'
import { OrderQtyInputComponent } from '@shared/components/trading/orders/common/order-qty-input/order-qty-input.component'
import { OrderPriceInputComponent } from '@shared/components/trading/orders/common/order-price-input/order-price-input.component'

@UseLocales({ ru, en })
@Component({
	selector: 'app-position-option-close',
	templateUrl: './position-option-close.component.html',
	styleUrl: './position-option-close.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		TuiIcon,
		TranslatePipe,
		HighlightDirective,
		UpperCasePipe,
		DatePipe,
		ReactiveFormsModule,
		TuiInputModule,
		TuiTextfieldControllerModule,
		OrderTypeSelectComponent,
		PricePipe,
		QuantityPipe,
		ButtonComponent,
		OrderQtyInputComponent,
		OrderPriceInputComponent,
	],
})
export class PositionOptionCloseComponent implements OnInit, OnDestroy {
	protected readonly math: Math = Math
	protected readonly marketStatusEnum: typeof MarketStatusEnum = MarketStatusEnum
	protected readonly optionTypeEnum: typeof OptionTypeEnum = OptionTypeEnum
	protected readonly orderTypeEnum: typeof OrderTypeEnum = OrderTypeEnum
	protected readonly orderTermEnum: typeof OrderTermEnum = OrderTermEnum
	protected readonly assetClassEnum: typeof AssetClassEnum = AssetClassEnum
	protected readonly optionSharesPerContractConstant: number = OptionSharesPerContractConstant
	protected readonly orderTypes: OrderTypeEnum[] = [this.orderTypeEnum.MARKET, this.orderTypeEnum.LIMIT]

	public position: InputSignal<PositionOptionInterface | null> = input<PositionOptionInterface | null>(null)
	public closeDropdown: OutputEmitterRef<void> = output<void>()

	private _destroy$: Subject<void> = new Subject<void>()

	private readonly _formBuilder: FormBuilder = inject<FormBuilder>(FormBuilder)
	private readonly _optionsService: OptionsService = inject<OptionsService>(OptionsService)
	private readonly _marketStatusHelperService: MarketStatusHelperService =
		inject<MarketStatusHelperService>(MarketStatusHelperService)
	private readonly _alertService: AlertService = inject<AlertService>(AlertService)
	private readonly _translateService: TranslateService = inject<TranslateService>(TranslateService)
	private readonly _alpacaAccountHelperService: AlpacaAccountHelperService =
		inject<AlpacaAccountHelperService>(AlpacaAccountHelperService)
	private readonly _positionsOptionsService: PositionsOptionsService =
		inject<PositionsOptionsService>(PositionsOptionsService)

	protected alpacaAccount: WritableSignal<AlpacaAccountInterface | null> = signal<AlpacaAccountInterface | null>(null)

	protected optionContractDetails: WritableSignal<OptionContractDetailsInterface | null> =
		signal<OptionContractDetailsInterface | null>(null)
	protected isLoadingOptionContractDetails: WritableSignal<boolean> = signal<boolean>(false)
	protected isLoadingClosePosition: WritableSignal<boolean> = signal<boolean>(false)

	protected form: FormGroup | null = null

	get marketStatus(): MarketStatusEnum {
		return this._marketStatusHelperService.marketStatus
	}

	get isInvalidPosition(): boolean {
		const formType: OrderTypeEnum | undefined = this.form?.get('type')?.value

		return (
			this.form?.invalid ||
			this.isLoadingClosePosition() ||
			(formType === this.orderTypeEnum.MARKET && this.marketStatus !== this.marketStatusEnum.marketOpen)
		)
	}

	get isSelectedLimitPrice(): boolean {
		return this.form?.get('type')?.value === this.orderTypeEnum.LIMIT
	}

	get totalSum(): number {
		const position: PositionOptionInterface | null = this.position()
		const form: ClosePositionInterface | undefined = this.form?.value
		if (!form || !position) return 0

		const isMarketOrder: boolean = form.type === this.orderTypeEnum.MARKET
		const price: number = isMarketOrder ? +position.current_price : +(form.limit_price ?? 0)

		return this.math.abs(price * +form.qty * this.optionSharesPerContractConstant)
	}
	private _positionForClose(): ClosePositionInterface | null {
		const position: PositionOptionInterface | null = this.position()
		if (!position || !this.form) return null

		const formValue: ClosePositionInterface = this.form.value

		return {
			symbol: position.symbol,
			type: formValue.type,
			qty: formValue.qty,
			...(this.isSelectedLimitPrice && { limit_price: formValue.limit_price }),
			time_in_force: this.orderTermEnum.DAY,
			asset_class: this.assetClassEnum.US_OPTION,
			cancel_open_orders: false,
		}
	}

	private _initForm(): void {
		this.form = this._formBuilder.group({
			type: [this.orderTypeEnum.LIMIT],
			limit_price: [0],
			qty: [this.math.abs(+(this.position()?.qty_available ?? 0))],
		})
	}

	private _fillForm(): void {
		if (!this.form) return

		this.form.patchValue({
			limit_price: this.optionContractDetails()?.results?.last_quote?.bid,
		})
	}

	private _listenTypeControl(): void {
		this.form
			?.get('type')
			?.valueChanges.pipe(takeUntil(this._destroy$))
			.subscribe((type: OrderTypeEnum): void => {
				if (type === this.orderTypeEnum.MARKET && this.marketStatus !== this.marketStatusEnum.marketOpen) {
					this._alertService.warning(this._translateService.instant('closeOption.closeNotAvailable'))
				}
			})
	}

	private _resetForm(): void {
		if (!this.form) return

		this.form.patchValue({
			type: this.orderTypeEnum.LIMIT,
			limit_price: this.optionContractDetails()?.results?.last_quote?.bid,
			qty: this.math.abs(+(this.position()?.qty_available ?? 0)),
		})
	}

	private _listenAlpacaAccount(): void {
		this._alpacaAccountHelperService.alpacaAccount$
			.pipe(takeUntil(this._destroy$))
			.subscribe((alpacaAccount: AlpacaAccountInterface | null): void => {
				this.alpacaAccount.set(alpacaAccount)
			})
	}

	private _getOptionContractDetails(): void {
		const position: PositionOptionInterface | null = this.position()
		if (!position) return

		const params: OptionContractDetailsParamsInterface = {
			option_ticker: `O:${position.symbol}`,
			asset_ticker: position.option_ticker,
		}

		this.isLoadingOptionContractDetails.set(true)
		this._optionsService
			.getOptionContractDetails(params)
			.pipe(
				takeUntil(this._destroy$),
				finalize((): void => this.isLoadingOptionContractDetails.set(false)),
			)
			.subscribe(
				(optionContractDetails: OptionContractDetailsInterface | null): void => {
					this.optionContractDetails.set(optionContractDetails)
					this._fillForm()
				},
				(error: HttpErrorResponse): void => {
					this._alertService.error(
						error.error.error.message ??
							this._translateService.instant('closeOption.optionContractDetailReject'),
					)
				},
			)
	}

	protected onClosePositionOption(): void {
		const closePositionForConfirm: ClosePositionInterface | null = this._positionForClose()

		const isOpenMarket: boolean = this.marketStatus === this.marketStatusEnum.marketOpen
		const isMarketOrder: boolean = closePositionForConfirm?.type === this.orderTypeEnum.MARKET

		if (!closePositionForConfirm) return

		if (!isOpenMarket && isMarketOrder) {
			this._alertService.error(this._translateService.instant('closeOption.marketNotOpenedReject'))
			return
		}

		this.isLoadingClosePosition.set(true)
		this._positionsOptionsService
			.closePositionOption(closePositionForConfirm)
			.pipe(
				takeUntil(this._destroy$),
				finalize((): void => this.isLoadingClosePosition.set(false)),
			)
			.subscribe(
				(): void => {
					this._alertService.success(this._translateService.instant('closeOption.closeOptionSuccess'))
					this._resetForm()
					this.onCloseDropdown()
				},
				(error: HttpErrorResponse): void => {
					this._alertService.error(
						error.error.error.message ?? this._translateService.instant('closeOption.closeOptionReject'),
					)
				},
			)
	}

	protected onCloseDropdown(): void {
		this.closeDropdown.emit()
	}

	ngOnInit(): void {
		this._listenAlpacaAccount()
		this._initForm()
		this._listenTypeControl()
		this._getOptionContractDetails()
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
